import { NJIconButton, NJToggle } from '@engie-group/fluid-design-system-react';
import type { ReactNode } from 'react';
import { Size2MixExpandCollapseBar, Size2MixInfoGrid, Size2MixSection, useSize2MixRefNavigation, useSectionsToggle } from '@shared/size2mix-shell';
import type { OptimisationDetail } from './types';

const SECTIONS = ['general', 'inputs', 'production', 'topology', 'advanced'] as const;

export default function ParametersTab({ detail }: { detail: OptimisationDetail }) {
    const nav = useSize2MixRefNavigation();
    const { bind, expandAll, collapseAll } = useSectionsToggle(SECTIONS);
    const p = detail.parameters;
    const planningLink = (label: string) => (
        <a href="#" className="nj-link nj-link--sm" onClick={(e) => { e.preventDefault(); nav.goTo('plannings'); }}>
            {label}
        </a>
    );

    return (
        <>
            <Size2MixExpandCollapseBar onExpandAll={expandAll} onCollapseAll={collapseAll} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Size2MixSection title="Informations générales" {...bind('general')}>
                    <CompactGrid
                        rows={[
                            { label: 'Nom', value: p.name },
                            ...(p.horizon === 'Paramétrique atemporelle' ? [] : [{ label: "Heure de début de l'optimisation", value: p.start }]),
                            { label: 'Nombre de pas', value: p.steps },
                            ...(p.horizon === 'Paramétrique atemporelle' ? [] : [{ label: 'Nombre de pas avec résultats détaillés', value: p.detailedSteps }]),
                            { label: 'Horizon', value: p.horizon },
                            { label: 'Granularité', value: p.granularity },
                            { label: 'Pilotage', value: <ReadonlySegmented options={['Pilotage par consommation', 'Pilotage par production']} value={p.driving} /> },
                            {
                                label: 'Mode de régulation des sous-stations',
                                value: <ReadonlySegmented options={['Delta de température', 'Température de sortie']} value={p.substationRegulation} />,
                            },
                            { label: 'Type de foisonnement', value: <ReadonlySegmented options={['Automatique', 'Canalisation', 'Consommation']} value={p.diversityType} /> },
                            { label: 'Coefficient de foisonnement global', value: p.diversityCoefficient },
                            {
                                label: 'Dimensionnement automatique des canalisations',
                                value: <NJToggle ariaLabel="Dimensionnement automatique des canalisations — état non relevé" checked={p.automaticPipeSizing} isDisabled />,
                            },
                        ]}
                    />
                </Size2MixSection>
                <Size2MixSection title="Données d'entrée" {...bind('inputs')}>
                    <CompactGrid rows={p.inputs.map(({ label, file }) => ({ label, value: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>{file.replace('.', ' . ')}<NJIconButton icon="download" label={file.split('.')[0]} variant="tertiary" scale="xs" /></span> }))} />
                </Size2MixSection>
                <Size2MixSection title="Planning de production" {...bind('production')}>
                    <Size2MixInfoGrid rows={[{ label: 'Planning utilisé', value: planningLink(p.productionPlanning) }]} />
                </Size2MixSection>
                <Size2MixSection title="Planning de topologie" {...bind('topology')}>
                    <Size2MixInfoGrid rows={[{ label: 'Planning utilisé', value: planningLink(p.topologyPlanning) }]} />
                </Size2MixSection>
                <Size2MixSection title="Options avancées" {...bind('advanced')}>
                    <CompactGrid rows={[{ label: 'Aucune option avancée activée', value: '' }]} />
                </Size2MixSection>
            </div>
        </>
    );
}

/** Measured 30/70 columns; labels may span multiple 24px lines. */
function CompactGrid({ rows }: { rows: Array<{ label: string; value: ReactNode }> }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '30% 70%', rowGap: 16, fontSize: 16, lineHeight: '24px', alignItems: 'center' }}>
            {rows.map((r) => (
                <div key={r.label} style={{ display: 'contents' }}>
                    <span>{r.label}</span>
                    <span style={{ color: 'var(--nj-semantic-color-text-neutral-primary-default)' }}>{r.value}</span>
                </div>
            ))}
        </div>
    );
}

/** Read-only segmented control as displayed in production (selected option on white, others greyed). */
function ReadonlySegmented({ options, value }: { options: string[]; value: string }) {
    return (
        <span role="radiogroup" aria-readonly style={{ display: 'inline-flex', padding: 4, background: 'var(--nj-core-color-grey-200)', borderRadius: 3, height: 32, boxSizing: 'border-box' }}>
            {options.map((o) => {
                const active = o === value;
                return (
                    <span
                        key={o}
                        role="radio"
                        aria-checked={active}
                        style={{
                            padding: '3px 12px',
                            fontSize: 12,
                            lineHeight: '18px',
                            fontWeight: active ? 700 : 400,
                            background: active ? 'var(--nj-semantic-color-background-neutral-primary-default)' : 'transparent',
                            color: active ? 'var(--nj-semantic-color-text-neutral-primary-default)' : 'var(--nj-semantic-color-text-neutral-tertiary-default)',
                        }}
                    >
                        {o}
                    </span>
                );
            })}
        </span>
    );
}
